"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"

type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("relative p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row flex-wrap justify-center gap-4",
        month: "flex flex-col gap-4 shrink-0",
        month_caption: "flex items-center justify-center px-9 pt-1 w-full",
        caption_label: "text-sm font-semibold",
        nav: "absolute inset-x-3 top-4 flex items-center justify-between",
        button_previous:
          "inline-flex items-center justify-center size-7 rounded-md border border-theme-surface-border bg-transparent p-0 text-foreground hover:bg-accent disabled:opacity-30 disabled:pointer-events-none",
        button_next:
          "inline-flex items-center justify-center size-7 rounded-md border border-theme-surface-border bg-transparent p-0 text-foreground hover:bg-accent disabled:opacity-30 disabled:pointer-events-none",
        month_grid: "border-collapse",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md size-9 font-normal text-[0.8rem] flex items-center justify-center",
        week: "flex mt-2",
        day: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent",
        day_button:
          "inline-flex items-center justify-center size-9 rounded-md p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground",
        range_start:
          "day-range-start aria-selected:bg-primary aria-selected:text-primary-foreground",
        range_end:
          "day-range-end aria-selected:bg-primary aria-selected:text-primary-foreground",
        selected:
          "bg-theme-button-primary-background text-theme-button-primary-foreground hover:bg-theme-button-primary-background hover:text-theme-button-primary-foreground focus:bg-theme-button-primary-background focus:text-theme-button-primary-foreground rounded-md",
        today: "bg-accent text-accent-foreground rounded-md",
        outside:
          "day-outside text-muted-foreground aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-40",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, ...rest }) => {
          if (orientation === "left") {
            return <ChevronLeft className="size-4" {...rest} />
          }
          return <ChevronRight className="size-4" {...rest} />
        },
      }}
      {...props}
    />
  )
}

export { Calendar }
